import React from 'react';
import { Button, makeStyles, Text, tokens } from '@fluentui/react-components';
import { rootUseStyles } from '../../../taskpane/fluent-style';
import { AlertBox } from '../../../components/Alert/AlertBox';
import InformationBox from './InformationBox';
import ReportPhishingButton from '../../common/report-phishing-button';
import RunningDeepScanButton from './RunningDeepScanButton';
import { AlertTypeChip } from '../../../components/Alert/AlertChip';
import { useTabStore } from '../../../providers/TabProvider';
import { InsightDataType } from '../../../types';

const useStyles = makeStyles({
    main: {
        flex: 1,
        alignContent: "center",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        overflow: "hidden",
    },
    section2: {
        padding: "0px 15px",
        display: "flex",
        flexDirection: "column",
        gap: "5px",
    },
});

interface InsightResultsContentProps {
    parsedAIResult: any;
    showActions?: boolean;
    insightData: Partial<InsightDataType>;
    isCheckingDeepScan?: boolean;
}

export default function InsightResultsContent({ parsedAIResult, showActions = true, insightData, isCheckingDeepScan }: InsightResultsContentProps) {
    const styles = useStyles();
    const { setActiveTab } = useTabStore();

    const errorMessage = typeof insightData?.ai_email_analysis_result === 'string' &&
        !parsedAIResult &&
        insightData.ai_email_analysis_result.length > 0 &&
        !insightData.ai_email_analysis_result.trim().startsWith('{')
        ? insightData.ai_email_analysis_result
        : null;

    if (errorMessage) {
        return (
            <section className={styles.main}>
                <div className={styles.section2}>
                    <AlertBox
                        type="error"
                        title="Analysis Failed"
                        description={errorMessage}
                    />
                </div>
            </section>
        )
    }

    return (
        <>
            <section
                style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: "15px",
                }}
                className={`${rootUseStyles().scrollArea} ${rootUseStyles().scrollbar}`}
            >
                <Text
                    as="p"
                    style={{
                        color: "#7B7B7B",
                        fontSize: "11px",
                        fontWeight: 500,
                        padding: "2px"
                    }}
                    className={rootUseStyles().paragraph}
                >
                    AI Insight completed at {insightData?.created_at ? new Date(insightData.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : ""}
                </Text>

                <AlertBox
                    type={parsedAIResult?.overall_ai_insight_result || "info" as AlertTypeChip}
                    title={parsedAIResult?.overall_ai_insight_result || "Clean"}
                    description={parsedAIResult?.why}
                    aiGenerated={true}
                />

                <InformationBox
                    status={parsedAIResult?.indicators_found?.status || "info" as AlertTypeChip}
                    alertChipContent={{
                        type: "indicator",
                        label: "Indicators found"
                    }}
                    contents={parsedAIResult?.indicators_found?.content || ""}
                />

                <InformationBox
                    status={parsedAIResult?.language_issues?.status || "info" as AlertTypeChip}
                    alertChipContent={{
                        type: "language",
                        label: "Language issues"
                    }}
                    contents={parsedAIResult?.language_issues?.content || ""}
                />

                <InformationBox
                    alertChipContent={{
                        type: "thinking",
                        label: "What you should do"
                    }}
                    contents={parsedAIResult?.what_you_should_do}
                />

            </section>
            {showActions && (
                <section className={styles.section2}>
                    <div style={{ display: "flex", gap: "5px" }}>
                        <ReportPhishingButton disableIcon={true} currentTab='ai-insight-result' />
                        <RunningDeepScanButton isCheckingDeepScan={isCheckingDeepScan} />
                    </div>
                    <Button
                        className={rootUseStyles().buttonInfoOutline}
                        onClick={() => setActiveTab("overview")}
                    >
                        Overview
                    </Button>
                </section>
            )}
        </>
    );
}
